// src/components/admin/AdminUserPermissions.tsx
import {
  Box,
  Checkbox,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
} from '@mui/material';
import BackendService from 'BackendService';
import { PermissionDefinition, Permissions, UserPermissions } from 'Permission';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Palette } from 'Palette';
import { StyledButton, StyledButtonPanel } from '../styles/StyledButtons';
import StyledModal from '../styles/StyledModal';
import StyledSection from '../styles/StyledSection';
import { StyledHeading, StyledList, StyledListItem, StyledTypography } from '../styles/styles';

interface AdminUserPermissionsState {
  allPermissions: PermissionDefinition[];
  userPermissions: UserPermissions;
  loadingAll: boolean;
  loadingUser: boolean;
  saving: boolean;
  error: string | null;
  changesMade: boolean;
  name?: string;
}

const AdminUserPermissions: React.FC = () => {
  const { adminUserId } = useParams<{ adminUserId: string }>();
  const navigate = useNavigate();
  const backendService = useMemo(() => new BackendService(), []);

  const [state, setState] = useState<AdminUserPermissionsState>({
    allPermissions: [],
    userPermissions: {} as UserPermissions,
    loadingAll: true,
    loadingUser: true,
    saving: false,
    error: null,
    changesMade: false,
  });

  useEffect(() => {
    if (!adminUserId) {
      setState((s) => ({
        ...s,
        error: 'Admin User ID is missing.',
        loadingAll: false,
        loadingUser: false,
      }));
      return;
    }

    const fetchPermissions = async () => {
      try {
        setState((s) => ({
          ...s,
          loadingAll: true,
          loadingUser: true,
          error: null,
        }));
        const [allPerms, userPerms, adminUser] = await Promise.all([
          backendService.getAllPermissions(),
          backendService.getAdminUserPermissions(adminUserId),
          backendService.getAdminUser(adminUserId),
        ]);

        setState((s) => ({
          ...s,
          allPermissions: allPerms,
          userPermissions: userPerms,
          loadingAll: false,
          loadingUser: false,
          name: adminUser.name,
        }));
      } catch (err: any) {
        console.error('Error fetching permissions:', err);
        setState((s) => ({
          ...s,
          error: err.message || 'Failed to load permissions. Check API/backend.',
          loadingAll: false,
          loadingUser: false,
        }));
      }
    };

    fetchPermissions();
  }, [adminUserId, backendService]);

  const handlePermissionChange = (permissionName: Permissions, checked: boolean) => {
    setState((prevState) => {
      const updated = { ...prevState.userPermissions };
      if (checked) updated[permissionName] = true;
      else delete updated[permissionName];

      return {
        ...prevState,
        userPermissions: updated,
        changesMade: true,
      };
    });
  };

  const handleSavePermissions = async () => {
    if (!adminUserId) return;
    setState((s) => ({ ...s, saving: true, error: null }));
    try {
      await backendService.updateAdminUserPermissions(adminUserId, state.userPermissions);
      setState((s) => ({ ...s, saving: false, changesMade: false }));
    } catch (err: any) {
      setState((s) => ({
        ...s,
        error: err.message || 'Failed to save permissions.',
        saving: false,
      }));
    }
  };

  if (state.loadingAll || state.loadingUser) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // --- Optional grouping logic ---
  const groupPermissions = () => {
    const groups: Record<string, PermissionDefinition[]> = {
      'Fund Opportunity': [],
      'Content Management': [],
      'Contact Management': [],
      'Document Access': [],
      'Investor Management': [],
      Other: [],
      'Admin User Management': [],
    };

    for (const perm of state.allPermissions) {
      if (perm.name.match(/Content|AI Content|Publication/i)) {
        groups['Content Management'].push(perm);
      } else if (perm.name.match(/Fund|Opportunity/i)) {
        groups['Fund Opportunity'].push(perm);
      } else if (perm.name.match(/Contact/i)) {
        groups['Contact Management'].push(perm);
      } else if (perm.name.match(/Document/i)) {
        groups['Document Access'].push(perm);
      } else if (perm.name.match(/Admin User/i)) {
        groups['Admin User Management'].push(perm);
      } else if (perm.name.match(/Investor/i)) {
        groups['Investor Management'].push(perm);
      } else {
        groups['Other'].push(perm);
      }
    }

    return groups;
  };
  const grouped = groupPermissions();

  return (
    <Container maxWidth="md">
      <StyledSection tone="default" sx={{ mt: 4 }} bottomSpacing={4}>
        <StyledHeading tone="default">Manage Permissions</StyledHeading>
        {state.name && (
          <StyledTypography variant="subtitle1" gutterBottom>
            For Admin User: {state.name}
          </StyledTypography>
        )}

        <StyledModal
          open={!!state.error}
          onClose={() => setState((s) => ({ ...s, error: null }))}
          title="Error"
          type="danger"
          tone="default"
        >
          <StyledTypography tone="default">{state.error}</StyledTypography>
        </StyledModal>

        {Object.entries(grouped)
          .filter(([, perms]) => perms.length > 0)
          .sort(([a], [b]) => {
            if (a === 'Admin User Management') return 1;
            if (b === 'Admin User Management') return -1;
            return 0;
          })
          .map(([groupLabel, permissions]) => (
            <Box key={groupLabel} sx={{ mt: 4 }}>
              <StyledTypography variant="h6" sx={{ mb: 1 }}>
                {groupLabel}
              </StyledTypography>
              <StyledList>
                {permissions.map((permission) => {
                  const isChecked = !!state.userPermissions[permission.name];
                  return (
                    <StyledListItem key={permission.name} dense>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isChecked}
                            onChange={(e) =>
                              handlePermissionChange(permission.name, e.target.checked)
                            }
                            disabled={state.saving}
                          />
                        }
                        label={
                          <Box>
                            <StyledTypography variant="body1">{permission.name}</StyledTypography>
                            <StyledTypography
                              variant="caption"
                              sx={{
                                display: 'block',
                                fontSize: '0.75rem',
                                lineHeight: 1.4,
                                mt: '2px',
                                color: Palette.default.inactiveOrCancelButtonText,
                              }}
                            >
                              {permission.description}
                            </StyledTypography>{' '}
                          </Box>
                        }
                      />
                    </StyledListItem>
                  );
                })}
              </StyledList>
              <Divider sx={{ mt: 2 }} />
            </Box>
          ))}
        <StyledButtonPanel buttonPosition="right" sx={{ mt: 4 }}>
          <StyledButton
            buttonVariant="inactiveOrCancel"
            onClick={() => navigate(-1)}
            disabled={state.saving}
          >
            Cancel
          </StyledButton>
          <StyledButton
            buttonVariant="default"
            onClick={handleSavePermissions}
            disabled={state.saving || !state.changesMade}
          >
            {state.saving ? (
              <CircularProgress size={24} sx={{ color: Palette.default.buttonText }} />
            ) : state.changesMade ? (
              'Save Permissions'
            ) : (
              'Saved ✓'
            )}
          </StyledButton>
        </StyledButtonPanel>
      </StyledSection>
    </Container>
  );
};

export default AdminUserPermissions;
